// U5 tone and length as a diff, under a draft (a card's draft, or the composer's message):
// Shorter · Warmer · Firmer · More formal. Each runs one small rewrite of the text on screen; the
// result shows as a word-level diff the owner accepts or rejects. It never writes over the owner's
// own edits: if the draft changed while the rewrite ran, or changes while the diff is shown, the
// rewrite is set aside with a note.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import { TONE_LABELS, TONES, type Tone } from "@shared/unstick";
import { isApiError } from "@web/lib/api";
import { unstickApi } from "@web/lib/unstickApi";
import { Button } from "@web/ui/Button";
import { DiffView } from "./DiffView";
import { canAccept, CHANGED_MEANWHILE, settleRewrite, type ToneOutcome } from "./toneRewrite";

export interface ToneControlProps {
  /** The draft text on screen now. */
  text: string;
  /** Takes the accepted rewrite as the draft. */
  onAccept: (text: string) => void;
  /** Whose voice: the card's account, or the composer's. */
  cardId?: string | undefined;
  accountId?: string | undefined;
  disabled?: boolean;
  /** A line under the diff (the composer: formatting becomes plain text). */
  acceptNote?: string | undefined;
  /** Shown at the end of the buttons row (the dictation mic). */
  trailing?: ReactNode;
}

type State =
  | { kind: "idle" }
  | { kind: "working"; tone: Tone }
  | Extract<ToneOutcome, { kind: "offer" }>
  | Extract<ToneOutcome, { kind: "note" }>;

export function ToneControl({
  text,
  onAccept,
  cardId,
  accountId,
  disabled,
  acceptNote,
  trailing,
}: ToneControlProps) {
  const [raw, setState] = useState<State>({ kind: "idle" });
  const latest = useRef(text);
  useEffect(() => {
    latest.current = text;
  }, [text]);
  // The draft changed under a diff on screen: the diff no longer describes it.
  const state: State =
    raw.kind === "offer" && !canAccept(raw, text)
      ? { kind: "note", message: CHANGED_MEANWHILE }
      : raw;

  const run = async (tone: Tone) => {
    const base = text;
    if (!base.trim() || state.kind === "working") return;
    setState({ kind: "working", tone });
    try {
      const result = await unstickApi.rewrite({
        text: base,
        tone,
        ...(cardId ? { cardId } : {}),
        ...(accountId ? { accountId } : {}),
      });
      setState(settleRewrite({ tone, base, current: latest.current, result }));
    } catch (error) {
      setState({
        kind: "note",
        message: isApiError(error, "invalid_request")
          ? "That draft can't be rewritten as it is."
          : "Couldn't rewrite it just now. Your draft is unchanged.",
      });
    }
  };

  const accept = () => {
    if (state.kind !== "offer") return;
    if (!canAccept(state, text)) {
      setState({ kind: "note", message: CHANGED_MEANWHILE });
      return;
    }
    onAccept(state.after);
    setState({ kind: "idle" });
  };

  return (
    <div className="flex flex-col gap-2" data-testid="tone-control">
      <div
        role="group"
        aria-label="Rewrite the draft"
        className="flex flex-wrap items-center gap-1"
      >
        {TONES.map((tone, i) => (
          <span key={tone} className="inline-flex items-center gap-1">
            {i > 0 ? (
              <span aria-hidden className="text-faint">
                ·
              </span>
            ) : null}
            <button
              type="button"
              data-tone={tone}
              disabled={disabled || state.kind === "working" || !text.trim()}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => void run(tone)}
              className="rounded-md px-1.5 py-0.5 text-sm text-muted hover:bg-sunk hover:text-ink disabled:opacity-60"
            >
              {TONE_LABELS[tone]}
            </button>
          </span>
        ))}
        {state.kind === "working" ? (
          <span className="ml-2 text-sm text-muted" aria-live="polite">
            Rewriting {TONE_LABELS[state.tone].toLowerCase()}…
          </span>
        ) : null}
        {trailing ? <span className="ml-auto">{trailing}</span> : null}
      </div>
      {state.kind === "offer" ? (
        <div className="flex flex-col gap-2" data-testid="tone-offer">
          <p className="text-sm text-muted">
            {TONE_LABELS[state.tone]}: underlined words are new, struck-through words go. Your draft
            stays as it is until you use this.
          </p>
          <DiffView before={state.before} after={state.after} />
          {acceptNote ? <p className="text-sm text-muted">{acceptNote}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onPointerDown={(e) => e.preventDefault()} onClick={accept}>
              <Check aria-hidden className="size-4" />
              Use this
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => setState({ kind: "idle" })}
            >
              <X aria-hidden className="size-4" />
              Keep mine
            </Button>
          </div>
        </div>
      ) : state.kind === "note" ? (
        <p className="text-sm text-muted" role="status" data-testid="tone-note">
          {state.message}{" "}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-ink"
            onClick={() => setState({ kind: "idle" })}
          >
            OK
          </button>
        </p>
      ) : null}
    </div>
  );
}
